/**
 * Period Allocation & Shift Helper
 *
 * Allows shifting a bank transaction 100% to another target month (e.g., September expense
 * paid in October), or splitting a lump-sum payment across multiple months (e.g., 2, 3, or 4
 * months for quarterly rent or AMC).
 *
 * Updates are broadcasted via liveRefresh so Banking, Cash Book, and Executive P&L update
 * immediately across tabs and components.
 */

import api from "./api";
import { notifyChange } from "./liveRefresh";

export const ALLOCATIONS_STORAGE_KEY = "lss_period_allocations";

/**
 * Read all saved period allocations from localStorage.
 * Returns an object keyed by statement line ID: { [line_id]: AllocationRecord }
 */
export function getPeriodAllocations() {
  try {
    const raw = localStorage.getItem(ALLOCATIONS_STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch (e) {
    console.error("Failed to read period allocations:", e);
    return {};
  }
}

/**
 * Get the allocation record for a specific statement line, or null.
 */
export function getAllocationForLine(lineId) {
  if (!lineId) return null;
  const all = getPeriodAllocations();
  return all[lineId] || null;
}

/**
 * Save or update a period allocation for a statement line.
 * Saves locally to localStorage (instant) and backs up to server via /banking/rules.
 */
export function savePeriodAllocation(lineId, data) {
  if (!lineId || !data) return;
  try {
    const all = getPeriodAllocations();
    all[lineId] = {
      ...data,
      line_id: lineId,
      updated_at: new Date().toISOString(),
    };
    localStorage.setItem(ALLOCATIONS_STORAGE_KEY, JSON.stringify(all));
    notifyChange();

    // Async server persistence (never throws)
    syncPeriodAllocationToServer(lineId, all[lineId]).catch(() => {});
  } catch (e) {
    console.error("Failed to save period allocation:", e);
  }
}

/**
 * Remove an allocation, reverting the line to its default bank statement date.
 */
export function removePeriodAllocation(lineId) {
  if (!lineId) return;
  try {
    const all = getPeriodAllocations();
    if (all[lineId]) {
      delete all[lineId];
      localStorage.setItem(ALLOCATIONS_STORAGE_KEY, JSON.stringify(all));
      notifyChange();

      // Async server deletion
      removeAllocationFromServer(lineId).catch(() => {});
    }
  } catch (e) {
    console.error("Failed to remove period allocation:", e);
  }
}

/**
 * Server synchronization helpers using existing /banking/rules backend endpoints
 */
export async function syncPeriodAllocationToServer(lineId, data) {
  try {
    const payload = {
      id: `alloc_${lineId}`,
      name: `[ALLOC]_${lineId}`,
      side: "both",
      match_all: true,
      conditions: [
        { field: "allocation", op: "equals", value: JSON.stringify(data) },
      ],
      ledger_id: data.ledger_id || "alloc_ledger",
      auto_apply: false,
    };
    await api.post("/banking/rules", payload);
  } catch (err) {
    // Non-blocking network sync
  }
}

export async function removeAllocationFromServer(lineId) {
  try {
    await api.delete(`/banking/rules/alloc_${lineId}`);
  } catch (err) {
    // Non-blocking
  }
}

export async function loadAllocationsFromServer() {
  try {
    const res = await api.get("/banking/rules");
    const rules = res.data?.rules || [];
    const fromServer = {};
    for (const r of rules) {
      if ((r.name || "").startsWith("[ALLOC]_") && r.conditions?.[0]?.value) {
        try {
          const val = r.conditions[0].value;
          const parsed = typeof val === "string" ? JSON.parse(val) : val;
          if (parsed && parsed.line_id) {
            fromServer[parsed.line_id] = parsed;
          }
        } catch (_) {}
      }
    }
    if (Object.keys(fromServer).length > 0) {
      const local = getPeriodAllocations();
      const merged = { ...local, ...fromServer };
      localStorage.setItem(ALLOCATIONS_STORAGE_KEY, JSON.stringify(merged));
      notifyChange();
    }
  } catch (err) {
    // Non-blocking
  }
}

/**
 * Dynamically expands statement lines according to their period allocations.
 *
 * If a line is split across multiple months (e.g. Sep: 15,000, Oct: 15,000, Nov: 15,000):
 * - When viewing September, it produces the September split line (Debit: 15,000).
 * - When viewing October, it produces the October split line (Debit: 15,000).
 * - When viewing November, it produces the November split line (Debit: 15,000).
 *
 * If a line is shifted 100% to another month (e.g. October shifted 100% to August):
 * - It appears in August with 100% of the debit.
 * - It is excluded from October's period view.
 */
export function expandPeriodLines(allLines, recFrom, recTo, allocations) {
  if (!allLines || !allLines.length) return [];

  const isDateInRange = (dateStr) => {
    const d = (dateStr || "").slice(0, 10);
    if (!d) return true;
    if (recFrom && d < recFrom) return false;
    if (recTo && d > recTo) return false;
    return true;
  };

  const isMonthInRange = (ym) => {
    if (!recFrom && !recTo) return true;
    const startM = (recFrom || "").slice(0, 7);
    const endM = (recTo || "").slice(0, 7);
    if (startM && ym < startM) return false;
    if (endM && ym > endM) return false;
    return true;
  };

  const result = [];

  for (const line of allLines) {
    const alloc = allocations ? allocations[line.id] : null;

    if (!alloc) {
      // Normal unallocated transaction
      if (isDateInRange(line.date)) {
        result.push(line);
      }
      continue;
    }

    const months = alloc.allocations || [];
    const origM = alloc.original_month || (line.date || "").slice(0, 7);

    if (alloc.type === "shift") {
      // 100% shifted to a target month
      const targetM = alloc.target_month || (months[0] ? months[0].month : origM);
      if (isMonthInRange(targetM)) {
        result.push({
          ...line,
          id: `${line.id}__alloc__${targetM}`,
          parent_id: line.id,
          date: targetM === origM ? line.date : `${targetM}-01`,
          is_allocated_shift: true,
          alloc_target_month: targetM,
          alloc_orig_month: origM,
          alloc_note: alloc.note,
          alloc_type: "shift",
          ledger_id: alloc.ledger_id || line.ledger_id,
          ledger_name: alloc.ledger_name || line.ledger_name,
        });
      }
      continue;
    }

    // Split across multiple months (e.g. 2, 3, 4+ months)
    for (let i = 0; i < months.length; i++) {
      const m = months[i];
      if (isMonthInRange(m.month)) {
        const amt = Number(m.amount) || 0;
        result.push({
          ...line,
          id: `${line.id}__alloc__${m.month}`,
          parent_id: line.id,
          date: m.month === origM ? line.date : `${m.month}-01`,
          debit: (line.debit || 0) > 0 ? amt : 0,
          credit: (line.credit || 0) > 0 ? amt : 0,
          is_allocated_split: true,
          split_index: i + 1,
          split_total_parts: months.length,
          split_month: m.month,
          split_amount: amt,
          total_original_amount: alloc.total_amount,
          alloc_orig_month: origM,
          alloc_note: alloc.note,
          alloc_type: "split",
          alloc_all_months: months,
          ledger_id: alloc.ledger_id || line.ledger_id,
          ledger_name: alloc.ledger_name || line.ledger_name,
        });
      }
    }
  }

  // Sort by date ascending, then id
  result.sort((a, b) => (a.date || "").localeCompare(b.date || "") || (a.id || "").localeCompare(b.id || ""));
  return result;
}

/**
 * Match a ledger name from a bank line/allocation to an executive OPEX bucket name.
 * e.g., "Marketing & Ads" -> "Marketing Expenses"
 *       "Rent" -> "Rent & Rates"
 *       "Laundry" -> "Laundry Expenses"
 */
export function matchOpexLine(candidateName, targetLedgerName) {
  if (!candidateName || !targetLedgerName) return false;
  const c = String(candidateName).toLowerCase().trim();
  const t = String(targetLedgerName).toLowerCase().trim();
  if (c === t) return true;

  // Specific keyword rules
  if (t.includes("marketing") || t.includes("advert") || t.includes("ad") || t.includes("promotion")) {
    if (c.includes("marketing") || c.includes("advert") || c.includes("promotion")) return true;
  }
  if (t.includes("rent")) {
    if (c.includes("rent & rates") || (c.includes("rent") && !c.includes("parking") && !c.includes("water"))) return true;
  }
  if (t.includes("laundry") || t.includes("dry clean") || t.includes("washing")) {
    if (c.includes("laundry")) return true;
  }
  if (t.includes("electric") || t.includes("power") || t.includes("bijli")) {
    if (c.includes("electricity")) return true;
  }
  if (t.includes("internet") || t.includes("telecom") || t.includes("broadband") || t.includes("wifi") || t.includes("airtel")) {
    if (c.includes("internet") || c.includes("telecom")) return true;
  }
  if (t.includes("welfare") || t.includes("refreshment") || t.includes("f&b")) {
    if (c.includes("client welfare") || c.includes("staff welfare")) return true;
  }
  if (t.includes("security")) {
    if (c.includes("security")) return true;
  }
  if (t.includes("housekeep") || t.includes("cleaning")) {
    if (c.includes("housekeeping")) return true;
  }
  if (t.includes("salary") || t.includes("payroll")) {
    if (t.includes("proprietor")) return c.includes("proprietor");
    if (c.includes("staff salary")) return true;
  }
  return false;
}

/**
 * Calculate the net OPEX shift adjustments for a given viewing month (e.g., "2026-09" or "2026-10").
 */
export function calculateMonthlyAdjustments(viewingMonth) {
  if (!viewingMonth) {
    return { inflows: [], outflows: [], ledgerDeltas: {}, netOpexDelta: 0 };
  }
  const ym = viewingMonth.slice(0, 7);
  const all = getPeriodAllocations();
  const inflows = [];
  const outflows = [];
  const ledgerDeltas = {};

  for (const [lineId, alloc] of Object.entries(all)) {
    const origM = (alloc.original_month || (alloc.original_date || "").slice(0, 7));
    const totalAmt = Number(alloc.total_amount) || 0;
    const ledger = (alloc.ledger_name || "Misc. Expenses").trim();
    const months = alloc.allocations || [];

    if (ym === origM) {
      // Payment physically occurred in this month.
      // What amount remains in this month?
      const stayAmt = months
        .filter((m) => m.month === ym)
        .reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
      const delta = Number((stayAmt - totalAmt).toFixed(2));
      if (Math.abs(delta) > 0.001) {
        const shiftedOut = Math.abs(delta);
        outflows.push({
          line_id: lineId,
          narration: alloc.narration,
          original_date: alloc.original_date,
          original_month: origM,
          ledger_id: alloc.ledger_id,
          ledger_name: ledger,
          total_amount: totalAmt,
          stay_amount: stayAmt,
          shifted_out_amount: shiftedOut,
          note: alloc.note || "",
          type: alloc.type,
          targets: months.filter((m) => m.month !== ym),
          all_months: months,
        });
        ledgerDeltas[ledger] = Number(((ledgerDeltas[ledger] || 0) + delta).toFixed(2));
      }
    } else {
      // Transaction was paid in a different month, but allocated here.
      const inflowAmt = months
        .filter((m) => m.month === ym)
        .reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
      if (inflowAmt > 0) {
        const partIdx = months.findIndex((m) => m.month === ym);
        inflows.push({
          line_id: lineId,
          narration: alloc.narration,
          original_date: alloc.original_date,
          original_month: origM,
          ledger_id: alloc.ledger_id,
          ledger_name: ledger,
          inflow_amount: inflowAmt,
          total_amount: totalAmt,
          note: alloc.note || "",
          type: alloc.type,
          split_index: partIdx >= 0 ? partIdx + 1 : 1,
          split_total_parts: months.length,
          all_months: months,
        });
        ledgerDeltas[ledger] = Number(((ledgerDeltas[ledger] || 0) + inflowAmt).toFixed(2));
      }
    }
  }

  const netOpexDelta = Number(Object.values(ledgerDeltas).reduce((s, d) => s + d, 0).toFixed(2));

  return {
    inflows,
    outflows,
    ledgerDeltas,
    netOpexDelta,
  };
}

/**
 * Format "2026-09" to "Sep 2026"
 */
export function formatMonthLabel(ym) {
  if (!ym) return "";
  const [yr, mo] = ym.split("-");
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const idx = parseInt(mo, 10) - 1;
  return `${monthNames[idx] || mo} ${yr}`;
}

/**
 * Return month formatted short, e.g. "Sep"
 */
export function formatMonthShort(ym) {
  if (!ym) return "";
  const [, mo] = ym.split("-");
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const idx = parseInt(mo, 10) - 1;
  return monthNames[idx] || mo;
}
